<template>
<div
	ref="ToastContainer"
	data-component="ToasterElement.vue"
	class="toast-container | fixed z-[8192]"
	:class="
		CustomPositionOnDesktop && ScreenSize > 1 ? CustomPositionOnDesktop :
		CustomPositionOnMobile && ScreenSize <= 1 ? CustomPositionOnMobile : 'bottom-0 right-0'
	"
	@animationend="onAnimationEnd"
/>
</template>

<script lang="ts">

type ToastElementStyle = 'info' | 'ok' | 'warn' | 'fail';

interface ToastElement {
	Message: string;
	Cancellable?: any;
	CancellableLabel?: string;
	Permanent?: boolean;
	Style?: ToastElementStyle;
	ButtonContent?: string;
	CustomPositionOnDesktop?: string;
	CustomPositionOnMobile?: string;
	IsSnackbar?: boolean;
	OnClose?: (source: string) => void;
	OnButtonClick?: () => void;
}

/**
 * Komponent odpowiedzialny za wyświetlenie powiadomienia w rogu ekranu.
 */
export default {
	name: 'ToasterElement',
	data() {
		return {
			CustomPositionOnDesktop: '',
			CustomPositionOnMobile: '',
			IsSnackbar: false,
		};
	},
	methods: {
		/**
		 * Wyświetla powiadomienie w rogu ekranu. Może przyjąć łańcuch znaków lub obiekt z polami:
		 * * `Message` - treść wiadomości
		 * * `Cancellable` - funkcja wywoływana po kliknięciu przycisku *Cofnij*
		 * * `Style` - zmień typ powiadomienia. Możliwe wartości to: `ok` - domyślna wartość, `fail` - operacja nie powiodła się
		 */
		Show(message: ToastElement | string) {
			// Przygotuj kontener i wiadomość
			const msg = typeof message === 'string' ? { Message: message } : message;
			const $container = this.$refs.ToastContainer as HTMLElement;

			this.CustomPositionOnDesktop = msg.CustomPositionOnDesktop || '';
			this.CustomPositionOnMobile = msg.CustomPositionOnMobile || '';
			this.IsSnackbar = msg.IsSnackbar || false;

			// Wyczyść istniejące powiadomienia
			$container.innerHTML = '';

			// Możliwe style powiadomienia
			const styles = {
				info: {
					color: 'info',
					icon: '<path d="M9.16667 12.9167H11.0917M10.1325 12.9167V9.375H9.17417M10.0002 6.66796C9.97272 6.66796 9.94639 6.67886 9.92697 6.69828C9.90756 6.71769 9.89665 6.74401 9.89665 6.77147V6.77277C9.89703 6.79331 9.90348 6.81328 9.91518 6.83017C9.92688 6.84706 9.94332 6.86011 9.96242 6.86767C9.98152 6.87524 10.0024 6.87699 10.0225 6.87271C10.0426 6.86842 10.061 6.85829 10.0754 6.84358C10.0897 6.82887 10.0994 6.81025 10.1032 6.79006C10.107 6.76987 10.1047 6.749 10.0967 6.73009C10.0886 6.71118 10.0752 6.69507 10.058 6.68379C10.0408 6.6725 10.0207 6.66655 10.0002 6.66667M17.5 10C17.5 14.1421 14.1421 17.5 10 17.5C5.85786 17.5 2.5 14.1421 2.5 10C2.5 5.85786 5.85786 2.5 10 2.5C14.1421 2.5 17.5 5.85786 17.5 10Z"/>',
				},
				warn: {
					color: 'warn',
					icon: '<path d="M10 10.4167V6.25M10.0002 13.3346C9.97272 13.3346 9.94639 13.3455 9.92697 13.3649C9.90756 13.3844 9.89665 13.4107 9.89665 13.4381V13.4394C9.89703 13.46 9.90348 13.4799 9.91518 13.4968C9.92688 13.5137 9.94332 13.5268 9.96242 13.5343C9.98152 13.5419 10.0024 13.5437 10.0225 13.5394C10.0426 13.5351 10.061 13.525 10.0754 13.5102C10.0897 13.4955 10.0994 13.4769 10.1032 13.4567C10.107 13.4365 10.1047 13.4157 10.0967 13.3968C10.0886 13.3778 10.0752 13.3617 10.058 13.3505C10.0408 13.3392 10.0207 13.3332 10.0002 13.3333M10 2.5C14.1425 2.5 17.5 5.8575 17.5 10C17.5 14.1425 14.1425 17.5 10 17.5C5.8575 17.5 2.5 14.1425 2.5 10C2.5 5.8575 5.8575 2.5 10 2.5Z"/>',
				},
				fail: {
					color: 'danger',
					icon: '<path d="M12.3583 7.64167L7.64167 12.3583M12.3583 12.3583L7.64167 7.64167M10 17.5C5.8575 17.5 2.5 14.1425 2.5 10C2.5 5.8575 5.8575 2.5 10 2.5C14.1425 2.5 17.5 5.8575 17.5 10C17.5 14.1425 14.1425 17.5 10 17.5Z"/>',
				},
				ok: {
					color: 'success',
					icon: '<path d="M16.6666 5.41669L7.49992 14.5834L3.33325 10.4167"/>',
				},
			};

			// Utwórz element powiadomienia i wypełnij go treścią
			const $n = document.createElement('div');
			const style = styles[msg.Style as ToastElementStyle] || styles['ok'];

			const $style = `toast-notification | ${msg.Permanent ? 'is-permanent' : 'is-not-permanent'} ${msg.Cancellable ? 'is-cancellable' : 'is-not-cancellable'} | shadow-lg rounded bg-white border-l border-l border-l-4 border-${style.color} text-sm flex items-center gap-4 p-4`;
			const $icon = this.IsSnackbar ? '' : `<svg class="text-${style.color} hidden md:block w-5 h-5 shrink-0" viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${style.icon}</svg>`;
			const $cancellable = msg.Cancellable ? `<div class="toast-action-button | font-bold text-${style.color} cursor-pointer px-5">${msg.CancellableLabel || 'Cofnij'}</div>` : '';
			const $closeable = msg.Permanent ? `
				<svg class="toast-close-button | w-8 h-8 -my-3 shrink-0 cursor-pointer" viewBox="0 0 32 32" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
					<path d="M10.6667 10.6667L21.3334 21.3334M21.3334 10.6667L10.6667 21.3334"/>
				</svg>
			` : '';
			const $buttonContent = msg.ButtonContent && this.ScreenSize > 1 ? `
				<div class="toast-cta-button cursor-pointer mx-5 font-bold text-${style.color}">${msg.ButtonContent}</div>
			` : '';

			$n.setAttribute('class', `is-entering ${$style}`);

			$n.innerHTML = `
					${$icon}
					<div class="toast-message | grow w-max">${msg.Message}</div>
					${$buttonContent}
					${$cancellable}
					${$closeable}
			`;

			$container.prepend($n);

			// Dodaj możliwe przyciski (akcji, zamknięcia)
			$n.addEventListener('click', (event) => {
				if (msg.OnClose) {
					msg.OnClose('click');
				}

				const { classList, parentNode } = event.target as HTMLElement;

				const isCloseButtonClicked = classList.contains('toast-close-button')
				|| (parentNode as HTMLElement)?.classList.contains('toast-close-button');
				const isCTAButtonClicked = classList.contains('toast-cta-button')
				|| (parentNode as HTMLElement)?.classList.contains('toast-cta-button');

				// jeżeli istnieje akcja OnButtonClick i jesteśmy na mobilce to wykonaj tą akcję upewniając się że user nie kliknął w przycisk zamknięcia
				if (this.ScreenSize <= 1 && msg.OnButtonClick && !isCloseButtonClicked
				) {
					msg.OnButtonClick();
				}

				// Jeżeli użytkownik kliknie w snackbar na desktopie to nie zamykaj go (nie licząc CTA i close buttona)
				if (msg.IsSnackbar && this.ScreenSize > 1 && !isCloseButtonClicked && !isCTAButtonClicked) {
					return;
				}

				$n.remove();
			});

			if (msg.Cancellable || msg.Permanent) {
				$n.querySelector('.toast-action-button')?.addEventListener('click', msg.Cancellable);
				$n.querySelector('.toast-close-button')?.addEventListener('click', () => {
					if (msg.OnClose) {
						msg.OnClose('button');
					}

					$n.remove();
				});
			}

			if (this.ScreenSize > 1 && msg.ButtonContent && msg.OnButtonClick) {
				$n.querySelector('.toast-cta-button')?.addEventListener('click', msg.OnButtonClick);
			}
		},

		/**
		 * Wymuszenie zamknięcia toasta
		 */
		Hide() {
			document.querySelector('.toast-container')?.remove();
		},

		/**
		 * Wewnętrzna metoda służąca do zmiany stanu wyświetlania powiadomienia (pokaż -> czekaj -> zniknij).
		 */
		onAnimationEnd(e: AnimationEvent) {
			const $t = e.target as HTMLElement;

			switch (e.animationName) {
			case 'toast-enter': $t.className = $t.className.replace('entering', 'staying'); break;
			case 'toast-stay': $t.className = $t.className.replace('staying', 'leaving'); break;
			case 'toast-leave': $t.remove(); break;
			default: break;
			}
		},
	},
};

</script>

<style lang="scss">
@keyframes toast-enter {
	from {
		opacity: 0;
		transform: translateY(16px);
	}

	to {
		opacity: 1;
		transform: translateY(0);
	}
}

@keyframes toast-stay {
	from {
		opacity: 1;
	}

	to {
		opacity: 1;
	}
}

@keyframes toast-leave {
	from {
		opacity: 1;
	}

	to {
		opacity: 0;
	}
}

.toast-container>* {
	position: absolute;
	right: 2rem;
	min-width: 24rem;
	transition: bottom 0.3s;
	animation: toast-enter 0.3s !important;
}

.toast-container>*.is-staying {
	animation: toast-stay 5s !important;
}

.toast-container>*.is-staying.is-permanent,
.toast-container>*.is-staying:hover {
	animation-play-state: paused !important;
}

.toast-container>*.is-leaving {
	animation: toast-leave 0.3s !important;
	animation-fill-mode: forwards;
	pointer-events: none;
}

.toast-container>*:nth-child(1) {
	bottom: calc(2rem + 4rem * 0);
	z-index: 4;
}

.toast-container>*:nth-child(2) {
	bottom: calc(2rem + 4rem * 1);
	z-index: 3;
}

.toast-container>*:nth-child(3) {
	bottom: calc(2rem + 4rem * 2);
	z-index: 2;
}

.toast-container>*:nth-child(4) {
	bottom: calc(2rem + 4rem * 3);
	z-index: 1;
}

@media (max-width: 767px) {
	.toast-container>* {
		width: calc(100vw - 1rem);
		min-width: 0;
		right: 0.5rem;
	}
}
</style>
